import { useCallback } from "react";
import { useCachedQuery } from "./useCachedQuery";
import { instanceBrowserSettingsResource } from "../lib/adminQueries";
import { readJson, serverErrorMessage } from "../lib/apiError";
import type { BrowserSettings, BrowserSettingsView } from "../lib/browserSettings";
import { getCsrfHeader } from "../lib/csrf";

export type SaveOutcome = { ok: true } | { ok: false; message: string };

export type UseBrowserSettingsResult = {
  status: "loading" | "ready" | "error";
  settings: BrowserSettingsView | null;
  error: string | null;
  save: (next: BrowserSettings) => Promise<SaveOutcome>;
};

const settingsUrl = (tenantId: string): string => `/api/admin/claws/${encodeURIComponent(tenantId)}/browser-settings`;

export const useBrowserSettings = (tenantId: string): UseBrowserSettingsResult => {
  const fetcher = useCallback(
    async (signal: AbortSignal): Promise<BrowserSettingsView> =>
      readJson<BrowserSettingsView>(await fetch(settingsUrl(tenantId), { credentials: "include", signal }), "Browser settings"),
    [tenantId],
  );
  const { status, data, error, mutate } = useCachedQuery<BrowserSettingsView>({
    cacheResource: instanceBrowserSettingsResource(tenantId),
    queryKey: settingsUrl(tenantId),
    fetcher,
  });
  const save = useCallback(async (next: BrowserSettings): Promise<SaveOutcome> => {
    try {
      const res = await fetch(settingsUrl(tenantId), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify(next),
      });
      if (!res.ok) return { ok: false, message: await serverErrorMessage(res, "Save failed") };
      const saved = await readJson<BrowserSettingsView>(res, "Browser settings");
      mutate(() => saved);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : String(err) };
    }
  }, [tenantId, mutate]);
  return { status, settings: data, error, save };
};
